import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../components/LanguageSelector';
import {
  BanknotesIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  GlobeAltIcon,
  ShieldCheckIcon,
  UsersIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { ThemeToggleButton } from '../components/ThemeToggleButton';

/* ─── Sub-components ─── */

const FeatureRow: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
}> = ({ icon, title, description }) => (
  <div className="flex gap-4">
    <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-lg bg-indigo-50 dark:bg-indigo-500/10">
      <div className="h-6 w-6 text-indigo-600 dark:text-indigo-400">{icon}</div>
    </div>
    <div>
      <h3 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h3>
      <p className="mt-1 text-sm leading-relaxed text-slate-500 dark:text-slate-400">{description}</p>
    </div>
  </div>
);

const PricingTier: React.FC<{
  name: string;
  price: string;
  description: string;
  features: string[];
  buttonText: string;
  highlighted?: boolean;
  highlightedBadge?: string;
  buttonLink: string;
}> = ({ name, price, description, features, buttonText, highlighted = false, highlightedBadge, buttonLink }) => (
  <div
    className={`flex flex-col rounded-2xl p-6 ${
      highlighted
        ? 'border-2 border-indigo-600 bg-white shadow-xl dark:border-indigo-500 dark:bg-surface-800'
        : 'border border-slate-200 bg-white shadow-sm dark:border-surface-700 dark:bg-surface-900'
    }`}
  >
    {highlighted && highlightedBadge && (
      <span className="mb-3 inline-block self-start rounded-full bg-indigo-100 px-3 py-0.5 text-xs font-semibold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300">
        {highlightedBadge}
      </span>
    )}
    <h3 className="text-lg font-semibold text-slate-900 dark:text-white">{name}</h3>
    <div className="mt-3 flex items-baseline">
      <span className="text-4xl font-extrabold text-slate-900 dark:text-white">{price}</span>
      {price !== 'Free' && <span className="ml-1 text-lg text-slate-500">/mo</span>}
    </div>
    <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">{description}</p>
    <ul className="mb-6 mt-6 flex-1 space-y-3">
      {features.map((feature, i) => (
        <li key={i} className="flex items-start gap-2.5">
          <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-indigo-500 dark:text-indigo-400" />
          <span className="text-sm text-slate-600 dark:text-slate-400">{feature}</span>
        </li>
      ))}
    </ul>
    <Link
      to={buttonLink}
      className={`mt-auto block rounded-lg px-4 py-2.5 text-center text-sm font-semibold transition-colors ${
        highlighted
          ? 'bg-indigo-600 text-white hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-400'
          : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-surface-700 dark:text-slate-200 dark:hover:bg-surface-600'
      }`}
    >
      {buttonText}
    </Link>
  </div>
);

/* ─── Main page ─── */

export const LandingPage: React.FC = () => {
  const { t } = useTranslation();

  const features = [
    { icon: <BanknotesIcon />, title: t('landing.features.budgeting.title'), description: t('landing.features.budgeting.description') },
    { icon: <UsersIcon />, title: t('landing.features.sharing.title'), description: t('landing.features.sharing.description') },
    { icon: <ChartBarIcon />, title: t('landing.features.insights.title'), description: t('landing.features.insights.description') },
    { icon: <CloudArrowUpIcon />, title: t('landing.features.sync.title'), description: t('landing.features.sync.description') },
    { icon: <GlobeAltIcon />, title: t('landing.features.i18n.title'), description: t('landing.features.i18n.description') },
    { icon: <ShieldCheckIcon />, title: t('landing.features.privacy.title'), description: t('landing.features.privacy.description') },
  ];

  const pricingTiers = [
    {
      name: t('landing.pricing.free.name'), price: t('landing.pricing.free.price'), description: t('landing.pricing.free.description'),
      features: [t('landing.pricing.free.feature1'), t('landing.pricing.free.feature2'), t('landing.pricing.free.feature3'), t('landing.pricing.free.feature4'), t('landing.pricing.free.feature5')],
      buttonText: t('landing.pricing.free.cta'), buttonLink: '/budgets/current',
    },
    {
      name: t('landing.pricing.premium.name'), price: t('landing.pricing.premium.price'), description: t('landing.pricing.premium.description'),
      features: [t('landing.pricing.premium.feature1'), t('landing.pricing.premium.feature2'), t('landing.pricing.premium.feature3'), t('landing.pricing.premium.feature4'), t('landing.pricing.premium.feature5')],
      buttonText: t('landing.pricing.premium.cta'), buttonLink: '/budgets/current', highlighted: true, highlightedBadge: t('landing.pricing.mostPopular'),
    },
    {
      name: t('landing.pricing.family.name'), price: t('landing.pricing.family.price'), description: t('landing.pricing.family.description'),
      features: [t('landing.pricing.family.feature1'), t('landing.pricing.family.feature2'), t('landing.pricing.family.feature3'), t('landing.pricing.family.feature4'), t('landing.pricing.family.feature5')],
      buttonText: t('landing.pricing.family.cta'), buttonLink: '/budgets/current',
    },
  ];

  const testimonials = [
    { quote: t('landing.testimonials.quote1'), author: t('landing.testimonials.author1'), role: t('landing.testimonials.role1') },
    { quote: t('landing.testimonials.quote2'), author: t('landing.testimonials.author2'), role: t('landing.testimonials.role2') },
    { quote: t('landing.testimonials.quote3'), author: t('landing.testimonials.author3'), role: t('landing.testimonials.role3') },
  ];

  const faqItems = [
    { q: t('landing.faq.q1'), a: t('landing.faq.a1') },
    { q: t('landing.faq.q2'), a: t('landing.faq.a2') },
    { q: t('landing.faq.q3'), a: t('landing.faq.a3') },
    { q: t('landing.faq.q4'), a: t('landing.faq.a4') },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-surface-950">
      {/* ── Header ── */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/80 backdrop-blur dark:border-surface-700 dark:bg-surface-900/80">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/landing" className="flex items-center gap-2 text-lg font-bold text-slate-800 dark:text-white">
            <img src="/logo.svg" alt="FairBalance" className="h-7 w-7" />
            <span className="hidden sm:inline">FairBalance</span>
          </Link>
          <div className="flex items-center gap-3">
            <ThemeToggleButton />
            <LanguageSelector />
          </div>
        </div>
      </header>

      {/* ── Hero ── */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-900 px-4 pb-24 pt-20 dark:from-surface-950 dark:via-surface-900 dark:to-indigo-950">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(99,102,241,0.15),transparent_70%)]" aria-hidden="true" />
        <div className="relative mx-auto max-w-3xl text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl">
            {t('landing.hero.catchphrase')}
          </h1>
          <p className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-slate-300">
            {t('landing.hero.subtitle')}
          </p>
          <div className="mt-10 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
            <Link
              to="/budgets/current"
              className="inline-block rounded-lg border border-transparent bg-indigo-500 px-8 py-3.5 text-base font-semibold text-white shadow-lg shadow-indigo-500/25 transition-colors hover:bg-indigo-400"
            >
              {t('landing.hero.primaryCta')}
            </Link>
            <a
              href="#how-it-works"
              className="inline-block rounded-lg border border-white/20 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-white/10"
            >
              {t('landing.hero.secondaryCta')}
            </a>
          </div>
          <p className="mt-8 text-sm text-slate-400">
            {t('landing.pricing.free.description')}
          </p>
        </div>
      </section>

      {/* ── Features ── */}
      <section className="px-4 py-20">
        <div className="mx-auto max-w-5xl">
          <div className="text-center">
            <h2 className="text-3xl font-extrabold text-slate-900 dark:text-white">
              {t('landing.featuresSection.title')}
            </h2>
            <p className="mx-auto mt-3 max-w-xl text-base text-slate-500 dark:text-slate-400">
              {t('landing.featuresSection.subtitle')}
            </p>
          </div>
          <div className="mt-14 grid gap-x-12 gap-y-10 md:grid-cols-2">
            {features.map((f, i) => (
              <FeatureRow key={i} icon={f.icon} title={f.title} description={f.description} />
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section id="how-it-works" className="bg-slate-50 px-4 py-20 dark:bg-surface-900">
        <div className="mx-auto max-w-4xl">
          <h2 className="text-center text-3xl font-extrabold text-slate-900 dark:text-white">
            {t('landing.howItWorks.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-base text-slate-500 dark:text-slate-400">
            {t('landing.howItWorks.subtitle')}
          </p>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {[
              { num: '1', title: t('landing.howItWorks.step1.title'), desc: t('landing.howItWorks.step1.description') },
              { num: '2', title: t('landing.howItWorks.step2.title'), desc: t('landing.howItWorks.step2.description') },
              { num: '3', title: t('landing.howItWorks.step3.title'), desc: t('landing.howItWorks.step3.description') },
            ].map((step) => (
              <div key={step.num} className="rounded-2xl bg-white p-6 shadow-sm dark:bg-surface-800">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white dark:bg-indigo-500">
                  {step.num}
                </div>
                <h3 className="mt-4 text-base font-semibold text-slate-900 dark:text-white">{step.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-500 dark:text-slate-400">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section className="px-4 py-20">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-3xl font-extrabold text-slate-900 dark:text-white">
            {t('landing.testimonials.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-base text-slate-500 dark:text-slate-400">
            {t('landing.testimonials.subtitle')}
          </p>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {testimonials.map((tm, i) => (
              <div
                key={i}
                className={`rounded-2xl p-6 ${
                  i === 1
                    ? 'border-2 border-indigo-100 bg-indigo-50/50 shadow-md dark:border-indigo-500/20 dark:bg-indigo-500/5'
                    : 'bg-slate-50 dark:bg-surface-800'
                }`}
              >
                <p className="text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                  &ldquo;{tm.quote}&rdquo;
                </p>
                <div className="mt-5 flex items-center gap-3">
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-indigo-100 text-sm font-semibold text-indigo-600 dark:bg-indigo-500/20 dark:text-indigo-400">
                    {tm.author.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{tm.author}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{tm.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Pricing ── */}
      <section className="bg-slate-50 px-4 py-20 dark:bg-surface-900">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-center text-3xl font-extrabold text-slate-900 dark:text-white">
            {t('landing.pricing.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-center text-base text-slate-500 dark:text-slate-400">
            {t('landing.pricing.subtitle')}
          </p>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {pricingTiers.map((tier, i) => (
              <PricingTier key={i} {...tier} />
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="px-4 py-20">
        <div className="mx-auto max-w-2xl">
          <h2 className="text-center text-3xl font-extrabold text-slate-900 dark:text-white">
            {t('landing.faq.title')}
          </h2>
          <p className="mx-auto mt-3 max-w-lg text-center text-base text-slate-500 dark:text-slate-400">
            {t('landing.faq.subtitle')}
          </p>
          <div className="mt-10 divide-y divide-slate-200 dark:divide-surface-700">
            {faqItems.map((item, i) => (
              <details key={i} className="group py-4">
                <summary className="flex cursor-pointer select-none items-center justify-between text-base font-medium text-slate-900 dark:text-white [&::-webkit-details-marker]:hidden">
                  {item.q}
                  <svg className="ml-2 h-5 w-5 flex-shrink-0 text-slate-400 transition-transform group-open:rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate-500 dark:text-slate-400">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ── */}
      <section className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-4 py-16 dark:from-indigo-700 dark:to-indigo-600">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-extrabold text-white">{t('landing.cta.title')}</h2>
          <p className="mx-auto mt-3 max-w-lg text-lg text-indigo-100">{t('landing.cta.subtitle')}</p>
          <div className="mt-8">
            <Link
              to="/budgets/current"
              className="inline-block rounded-lg bg-white px-8 py-3.5 text-base font-semibold text-indigo-600 shadow-lg transition-colors hover:bg-indigo-50"
            >
              {t('landing.cta.button')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};
